import axios from 'axios';
import { updateAuth } from 'redux/actions/auth';
import { apiURL } from 'config';

// We inject the Redux store from the App at runtime to avoid circular imports.
let appStore;
let appHistory;

export const setAxiosStore = storeInstance => {
  appStore = storeInstance;
};

export const setAxiosHistory = historyInstance => {
  appHistory = historyInstance;
};

export function getStore() {
  if (!appStore) {
    // In tests or very early startup, we may not have a store yet.
    // Returning a dummy object avoids crashes; requests made before store is set
    // simply won't add auth headers or dispatch refresh actions.
    return {
      getState: () => ({ auth: {}, app: {} }),
      dispatch: () => {},
    };
  }
  return appStore;
}

axios.defaults.baseURL = apiURL;

// Add a request interceptor
axios.interceptors.request.use(
  config => {
    const { tokenType, accessToken, loggedIn } = getStore().getState().auth;
    const token = `${tokenType} ${accessToken}`;
    if (loggedIn) {
      config.headers['Authorization'] = token;
    }
    // Let browser set Content-Type (with boundary) for FormData
    if (!(config.data && config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  error => {
    Promise.reject(error);
  },
);

//Add a response interceptor

axios.interceptors.response.use(
  response => {
    return response;
  },
  function(error) {
    const originalRequest = error.config;
    
    // Check for network errors (no internet connection) or server errors (5xx including 502 Bad Gateway)
    // Network errors occur when there's no response object (connection failed)
    const isNetworkError = !error.response && (
      error.code === 'NETWORK_ERROR' || 
      error.code === 'ECONNABORTED' ||
      error.message === 'Network Error' || 
      error.message.includes('timeout') ||
      error.message.includes('Network request failed') ||
      error.message.includes('Bad Gateway')
    );
    
    // Check for server errors (5xx) including 502 Bad Gateway
    // Also handle cases where error.response might exist but status is 502
    const status = error.response ? error.response.status : null;
    const isServerError = status && status >= 500;
    const is502Error = status === 502 || error.message.includes('502') || error.message.includes('Bad Gateway');
    
    // Redirect to no-connection page if network error, server error, or 502 Bad Gateway
    // Skip redirect if already on no-connection page or if it's a no-connection check request
    if ((isNetworkError || isServerError || is502Error) && appHistory) {
      const currentPath = appHistory.location.pathname;
      const isNoConnectionPage = currentPath === '/no-connection';
      const isNoConnectionCheck = originalRequest && originalRequest.url && (
        originalRequest.url.includes('/health') || 
        originalRequest.url.includes('/no-connection')
      );
      
      if (!isNoConnectionPage && !isNoConnectionCheck) {
        // Store the current path so we can return to it after connection is restored
        appHistory.push({
          pathname: '/no-connection',
          state: { from: { pathname: currentPath } }
        });
        return Promise.reject(error);
      }
    }
    
    // Handle 401 errors for authentication
    if (error.response && error.response.status === 401) {
      if (
        originalRequest.url === `/auth/admin-signin` ||
        originalRequest.url === `/auth/admin-refresh-token`
      ) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        const { refreshToken, email } = getStore().getState().auth;
        return axios
          .post('/auth/admin-refresh-token', {
            email,
            refreshToken,
          })
          .then(response => {
            const { data: res } = response;
            if (res.code === 200) {
              getStore().dispatch(updateAuth({ ...res.token }));
              const { tokenType, accessToken } = res.token;
              axios.defaults.headers.common[
                'Authorization'
              ] = `${tokenType} ${accessToken}`;
              return axios(originalRequest);
            }
          });
      }
    }
    
    return Promise.reject(error);
  },
);

export default axios;
